import '@total-typescript/ts-reset'

import { createApp as createVueApp, h, type App } from 'vue'
import { createInertiaApp, type CreateInertiaAppProps } from '@shared/inertia'
import SensitiveContentRevealHost from '@shared/components/SensitiveContentRevealHost.vue'
import '@shared/axios'
import { initSentry } from '@shared/sentry'
import { initTracking } from '@shared/tracking'
import { preloadPageChunks } from '@shared/preloadPageChunks'
import { getCurrentPage } from '@shared/inertia'

export type SetupFn = (app: App) => void
export type InertiaOptions = Omit<CreateInertiaAppProps, 'resolve' | 'setup'>

export type AppName =
  | 'admin_phlex'
  | 'admin'
  | 'consumer'
  | 'consumer_phlex'
  | 'consumer_ssr'
  | 'corporate'
  | 'corporate_public'
  | 'internal'
  | 'redemption'
  | 'sandbox'

export function createApp (
  name: AppName,
  pages: Record<string, any>,
  setup?: SetupFn,
  options?: { createAppWith?: typeof createVueApp, preloadChunks?: boolean },
) {
  return createInertiaApp({
    progress: false,
    async resolve (pageName: string) {
      const page = pages[`./${pageName}.vue`]

      if (!page) {
        throw new Error(`
          Unknown page ${pageName}.

          Is it located under app/frontend/${name}/pages with a .vue extension?
        `)
      }

      if (import.meta.env.SSR || typeof page !== 'function')
        return page

      const storageKey = `chunk-reload:${pageName}`

      try {
        const resolvedPage = await page()
        sessionStorage.removeItem(storageKey)
        return resolvedPage
      }
      catch (error) {
        if (error instanceof TypeError && error.message.includes('dynamically imported module')) {
          if (!sessionStorage.getItem(storageKey)) {
            sessionStorage.setItem(storageKey, '1')
            window.location.reload()
            // Return a never-resolving promise so that, if the reload is delayed or blocked,
            // Inertia/Vue does not continue and attempt to render an undefined component.
            return new Promise<never>(() => {
              // Intentionally left empty: this promise never resolves.
            })
          }
        }
        throw error
      }
    },
    setup ({ plugin, App: InertiaApp, props, el }) {
      const createApp = options?.createAppWith || createVueApp
      const app = createApp({ name, render: () => h(InertiaApp, props) })
        .use(plugin)

      setup?.(app)

      initSentry(app, props)

      if (name !== 'admin')
        initTracking(name)

      if (el) {
        app.mount(el)
        document.documentElement.dataset.appHydrated = 'true'

        // A separate app, kept out of the Inertia root to avoid a hydration mismatch.
        if (!import.meta.env.SSR) {
          const host = document.body.appendChild(document.createElement('div'))
          createVueApp(SensitiveContentRevealHost).mount(host)

          if (options?.preloadChunks)
            preloadPageChunks(pages, getCurrentPage()?.version)
        }
      }

      return app
    },
    ...options,
  })
}
